// How presence reads in a row: the avatar's dot and one line of text, "Open to taste together" or
// "Active 3d ago". The relative time comes from Intl.RelativeTimeFormat in the reader's language;
// the buckets (just now under 5 minutes, nothing past 8 weeks) are the domain's.
import type { PresenceActivity, PresenceEntry } from "@proef/contract";
import { lastActiveBucket } from "@proef/domain";
import { translate, type Locale, type MessageKey } from "../i18n";
import type { AvatarPresence } from "../../ui";

type Translate = (key: MessageKey, vars?: Record<string, string | number>) => string;

/** "just now", "12m ago", "3d ago", "2w ago" for a last-seen time; undefined when it says nothing. */
export function formatLastActive(
  lastSeenAt: string | null,
  nowMs: number,
  locale: Locale,
): string | undefined {
  const bucket = lastActiveBucket(lastSeenAt, nowMs);
  if (bucket === undefined) return undefined;
  if (bucket.kind === "now") return translate(locale, "social.presence.justNow");
  return new Intl.RelativeTimeFormat(locale, { style: "narrow", numeric: "always" }).format(
    -bucket.value,
    bucket.unit,
  );
}

export interface PresenceView {
  /** The avatar's dot: tasting now, online while the app is open, scheduled around a session, none otherwise. */
  avatar: AvatarPresence | undefined;
  /** The row's text, so the dot is never the only signal. */
  line: string | undefined;
  /** The session to join from this row, when the person is tasting in one the viewer may join. */
  join?: { sessionId: string; name: string; right: "participant" | "group" } | undefined;
}

/** "Aan het proeven", with the shared tasting's name and the guided page when the edge says them. */
function tastingLine(activity: PresenceActivity, t: Translate): string {
  const base =
    activity.name === undefined
      ? t("social.presence.tasting")
      : t("social.presence.tastingAt", { name: activity.name });
  return activity.step === undefined
    ? base
    : t("social.presence.tastingStep", { base, step: t(`social.presence.step.${activity.step}`) });
}

export function presenceView(
  entry: PresenceEntry | undefined,
  nowMs: number,
  locale: Locale,
  t: Translate,
): PresenceView {
  const activity = entry?.activity;
  if (activity !== undefined) {
    return {
      avatar: "tasting",
      line: tastingLine(activity, t),
      join:
        activity.join !== undefined &&
        activity.sessionId !== undefined &&
        activity.name !== undefined
          ? { sessionId: activity.sessionId, name: activity.name, right: activity.join }
          : undefined,
    };
  }
  if (entry?.open === true) {
    if (entry.openVia !== "session") return { avatar: "online", line: t("social.presence.open") };
    // A session someone said yes to reads by where it stands: ahead, running, or just over.
    const phase = entry.sessionPhase ?? "soon";
    return {
      avatar: "scheduled",
      line: t(
        phase === "live"
          ? "social.presence.sessionLive"
          : phase === "after"
            ? "social.presence.sessionAfter"
            : "social.presence.session",
      ),
    };
  }
  const when = formatLastActive(entry?.lastSeenAt ?? null, nowMs, locale);
  return {
    avatar: undefined,
    line: when === undefined ? undefined : t("social.presence.lastActive", { when }),
  };
}
